import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, X, ClipboardPlus } from "lucide-react";
import { toast } from "sonner";

const toIso = (v) => (v ? new Date(v + ":00+07:00").toISOString() : null);

export default function CreateTaskDialog({ open, onOpenChange, onDone, defaultPosition, allowOrgWide = false }) {
  const [positions, setPositions] = useState([]);
  const [team, setTeam] = useState(null);
  const [scope, setScope] = useState("tim");
  const [form, setForm] = useState({
    title: "", description: "", definition_of_done: "", assignee_position_id: defaultPosition || "",
    priority: "normal", deadline_current: "", start_date: "", estimated_hours: 4,
    progress_mode: "checklist_rata", requires_verification: true, requires_proof: false,
  });
  const [checklist, setChecklist] = useState([{ text: "", weight: 0 }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/org/positions").then((r) => setPositions(r.data));
      api.get("/org/my-team").then((r) => setTeam(r.data));
    }
  }, [open]);

  const teamIds = new Set(team?.position_ids || []);
  const options = team?.all || (allowOrgWide && scope === "org") ? positions : positions.filter((p) => teamIds.has(p.id));
  const changeScope = (v) => { setScope(v); set("assignee_position_id", ""); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Judul wajib diisi");
    if (!form.assignee_position_id) return toast.error("Pilih penerima tugas");
    if (!form.deadline_current) return toast.error("Deadline wajib diisi");
    const pos = positions.find((p) => p.id === form.assignee_position_id);
    setLoading(true);
    try {
      await api.post("/tasks", {
        ...form,
        org_wide: allowOrgWide && scope === "org",
        deadline_current: toIso(form.deadline_current),
        start_date: toIso(form.start_date) || new Date().toISOString(),
        estimated_hours: Number(form.estimated_hours),
        division_id: pos?.division?.id || pos?.division_id || null,
        branch_id: pos?.branch?.id || pos?.branch_id || null,
        checklist: ["checklist_rata", "checklist_bobot"].includes(form.progress_mode)
          ? checklist.filter((c) => c.text.trim()).map((c) => ({ text: c.text, weight: Number(c.weight || 0) }))
          : [],
      });
      toast.success("Tugas berhasil dibuat");
      onDone?.();
      onOpenChange(false);
      setForm((f) => ({ ...f, title: "", description: "", definition_of_done: "" }));
      setChecklist([{ text: "", weight: 0 }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat tugas"); }
    setLoading(false);
  };

  const isChecklist = ["checklist_rata", "checklist_bobot"].includes(form.progress_mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto zoa-scroll">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardPlus className="h-5 w-5 text-indigo-500" />Buat Tugas Baru</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Judul</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" placeholder="Judul tugas" /></div>
          <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></div>
          <div><Label>Definition of Done</Label><Textarea value={form.definition_of_done} onChange={(e) => set("definition_of_done", e.target.value)} rows={2} /></div>
          {allowOrgWide && team && !team.all && (
            <div>
              <Label className="mb-1.5 block">Tugaskan ke</Label>
              <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="task-scope-toggle">
                {[["tim", "Tim bawahan"], ["org", "Seluruh organisasi"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => changeScope(k)} data-testid={`task-scope-${k}`}
                    className={`px-3 py-1.5 text-sm rounded-md font-semibold transition-colors ${scope === k ? "bg-white shadow text-indigo-700" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
                ))}
              </div>
              {scope === "org" && <p className="text-xs text-amber-600 mt-1">Tugas ke luar tim/divisi akan menunggu persetujuan kepala divisi penerima.</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Penerima (Posisi)</Label>
              <Select value={form.assignee_position_id} onValueChange={(v) => set("assignee_position_id", v)}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Pilih posisi" /></SelectTrigger>
                <SelectContent>
                  {options.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Belum ada posisi tersedia</div>}
                  {options.map((p) => <SelectItem key={p.id} value={p.id}>{p.title} — {p.holder?.name || "(kosong)"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioritas</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kritis">Kritis</SelectItem><SelectItem value="tinggi">Tinggi</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem><SelectItem value="rendah">Rendah</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Mulai</Label><Input type="datetime-local" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div><Label>Deadline</Label><Input type="datetime-local" value={form.deadline_current} onChange={(e) => set("deadline_current", e.target.value)} data-testid="task-deadline-input" /></div>
            <div><Label>Estimasi Jam</Label><Input type="number" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} /></div>
          </div>
          <div>
            <Label>Mode Progres</Label>
            <Select value={form.progress_mode} onValueChange={(v) => set("progress_mode", v)}>
              <SelectTrigger data-testid="task-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="checklist_rata">Otomatis — Checklist Rata</SelectItem>
                <SelectItem value="checklist_bobot">Otomatis — Checklist Berbobot</SelectItem>
                <SelectItem value="rollup_subtugas">Otomatis — Roll-up Sub-tugas</SelectItem>
                <SelectItem value="manual">Manual — Input Persentase</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isChecklist && (
            <div>
              <Label className="mb-1.5 block">Langkah / Checklist</Label>
              {checklist.map((c, i) => (
                <div key={i} className="flex gap-2 mb-1.5">
                  <Input value={c.text} onChange={(e) => setChecklist((a) => a.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} placeholder={`Langkah ${i + 1}`} />
                  {form.progress_mode === "checklist_bobot" && <Input type="number" className="w-24" value={c.weight} onChange={(e) => setChecklist((a) => a.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)))} placeholder="Bobot %" />}
                  {checklist.length > 1 && <Button type="button" variant="ghost" size="icon" onClick={() => setChecklist((a) => a.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setChecklist((a) => [...a, { text: "", weight: 0 }])}><Plus className="h-4 w-4 mr-1" />Tambah langkah</Button>
              {form.progress_mode === "checklist_bobot" && <p className="text-xs text-slate-400 mt-1">Total bobot harus 100%.</p>}
            </div>
          )}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div><Label>Wajib Verifikasi (Maker–Checker)</Label><p className="text-xs text-slate-400">Perlu persetujuan pemberi tugas saat selesai</p></div>
            <Switch checked={form.requires_verification} onCheckedChange={(v) => set("requires_verification", v)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div><Label>Wajib Bukti</Label><p className="text-xs text-slate-400">Lampiran diperlukan sebelum kirim verifikasi</p></div>
            <Switch checked={form.requires_proof} onCheckedChange={(v) => set("requires_proof", v)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={loading} data-testid="task-create-submit" className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Menyimpan..." : "Buat Tugas"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
